import type { DeviceResponseV5 } from '@emma/api_client/types';
import { getCompatibleOsVersionsForDevice } from '@emma/core/device';
import { Box, useToast } from '@lego/klik-ui';
import { Clone, Delete, Dispatched, QrCodeBold, Redo, Restart, Trash } from '@lego/klik-ui/icons';
import type { InferRequestType } from 'hono/client';
import { toDataURL } from 'qrcode';
import { useMemo, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { Loader, PageLoadError, SubNavigationBar } from '@/components/common';
import { BreadCrumb } from '@/components/common/BreadCrumb';
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { ConfirmationSelectionDialog } from '@/components/common/ConfirmationSelectionDialog';
import { DeviceNetworkConfigs, DeviceRolloutWindows, DeviceSshKeys } from '@/components/device';
import { DeviceDetails } from '@/components/device/DeviceDetails';
import { DeviceFileTable } from '@/components/file/DeviceFileTable';
import { type ApiClient, useMutation } from '@/hooks/api';
import { useDocumentTitle } from '@/hooks/common';
import { useDevice } from '@/hooks/device';
import { useCreateDeviceTransfer } from '@/hooks/device_transfer';
import { useCanOperateById, useCanWriteById, useWorkspaces } from '@/hooks/workspace';
import { useRegisterPageContext } from '@/providers/PageContextProvider';
import { DeviceActionLog } from '@/sub_pages/device/DeviceActionLog';
import { DeviceDeployments } from '@/sub_pages/device/DeviceDeployments';
import { paths } from '@/utility/global_paths';
import { ToastOptions } from '@/utility/ToastOptions';

const getWipeStorageTooltip = (reportedCoreVersion: string | null): string | undefined => {
  const version = Number.parseInt(reportedCoreVersion?.replace('v', '') ?? '', 10);
  if (!Number.isNaN(version) && version < 15) {
    return 'Wipe Persistent Storage requires core version 15 or above.';
  }
  return undefined;
};

type DeviceActionItemsParams = {
  canWrite: boolean;
  device: DeviceResponseV5;
  wipeStorageTooltip: string | undefined;
  wipeStorageUnsupported: boolean;
  navigate: ReturnType<typeof useNavigate>;
  setShowTransferModal: (v: boolean) => void;
  setShowRestartApplicationModal: (v: boolean) => void;
  setShowPruneModal: (v: boolean) => void;
  setShowRebootModal: (v: boolean) => void;
  setShowWipePersistentStorageModal: (v: boolean) => void;
  handleQrCodeGenerate: (device: DeviceResponseV5, filename: string) => Promise<void>;
};

const buildDeviceActionItems = ({
  canWrite,
  device,
  wipeStorageTooltip,
  wipeStorageUnsupported,
  navigate,
  setShowTransferModal,
  setShowRestartApplicationModal,
  setShowPruneModal,
  setShowRebootModal,
  setShowWipePersistentStorageModal,
  handleQrCodeGenerate,
}: DeviceActionItemsParams) => {
  const isNotProvisioned = device.state !== 'PROVISIONED';
  const deviceNotReady = isNotProvisioned || !device.isConnected || !device.core.reportedVersion;
  const deviceNotReadyTooltip = isNotProvisioned
    ? 'Device has not been provisioned yet.'
    : 'Device is either not connected or does not have reported deployment.';

  const deviceActionItems = [
    {
      label: 'Fleet Assignment',
      icon: <Dispatched />,
      onClick: () => navigate(paths.deviceActionFleetAssignment(device.id)),
      requiresWrite: true,
    },
    {
      label: 'Clone Device',
      icon: <Clone />,
      onClick: () => navigate(paths.deviceActionClone(device.id)),
    },
    {
      label: 'Transfer Device',
      icon: <Dispatched />,
      onClick: () => setShowTransferModal(true),
    },
    {
      label: 'Generate Device Label',
      icon: <QrCodeBold />,
      onClick: () => handleQrCodeGenerate(device, `device-${device.id}-info.png`),
    },
    {
      label: 'Restart Application',
      icon: <Redo />,
      onClick: () => setShowRestartApplicationModal(true),
      disabled: deviceNotReady,
      tooltip: deviceNotReady ? deviceNotReadyTooltip : undefined,
      color: 'warning.600',
      dividerBefore: true,
    },
    {
      label: 'Reboot Device',
      icon: <Restart />,
      onClick: () => setShowRebootModal(true),
      color: 'warning.600',
      tooltip: deviceNotReady ? deviceNotReadyTooltip : undefined,
      disabled: deviceNotReady,
    },
    {
      label: 'Prune Device',
      icon: <Trash />,
      onClick: () => setShowPruneModal(true),
      disabled: deviceNotReady,
      tooltip: deviceNotReady ? deviceNotReadyTooltip : undefined,
      requiresWrite: true,
      color: 'error.500',
      dividerBefore: true,
    },
    {
      label: 'Wipe Persistent Storage',
      icon: <Delete />,
      onClick: () => setShowWipePersistentStorageModal(true),
      disabled: deviceNotReady || wipeStorageUnsupported,
      tooltip: deviceNotReady ? deviceNotReadyTooltip : wipeStorageTooltip,
      requiresWrite: true,
      color: 'error.500',
    },
  ];

  return deviceActionItems
    .filter(({ requiresWrite }) => !requiresWrite || canWrite)
    .map(({ requiresWrite: _, ...item }) => item);
};

type PostDeviceAction = ApiClient['v1']['devices'][':deviceId']['actions']['$post'];
type DeviceActionBody = InferRequestType<PostDeviceAction>['json'];

const DevicePageContent = ({ deviceId }: { deviceId: number }) => {
  const navigate = useNavigate();
  const actionOnErr = () => navigate(paths.devices);
  const postAction = useMutation<unknown, DeviceActionBody>(
    `device-action-${deviceId}`,
    (client, json) =>
      client.v1.devices[':deviceId'].actions.$post({
        param: { deviceId: deviceId.toString() },
        json,
      })
  );
  const createDeviceTransfer = useCreateDeviceTransfer();
  const [showRestartApplicationModal, setShowRestartApplicationModal] = useState<boolean>(false);
  const [showPruneModal, setShowPruneModal] = useState<boolean>(false);
  const [showRebootModal, setShowRebootModal] = useState<boolean>(false);
  const [showWipePersistentStorageModal, setShowWipePersistentStorageModal] =
    useState<boolean>(false);
  // TODO(OSTree-Upgrade): This should be removed once all devices have been upgraded to Bootc
  const [showUpgradeOsModal, setShowUpgradeOsModal] = useState<boolean>(false);
  const [upgradeOsVersion, setUpgradeOsVersion] = useState<string>('');
  const [showTransferModal, setShowTransferModal] = useState<boolean>(false);
  const [targetWorkspaceId, setTargetWorkspaceId] = useState<number | undefined>(undefined);
  const [fleetId, setFleetId] = useState<number | undefined>(undefined);
  const toast = useToast();

  const { data: device, error, isLoading, mutate } = useDevice(deviceId);
  useRegisterPageContext(device);
  const canWrite = useCanWriteById(device?.workspaceId);
  const canOperate = useCanOperateById(device?.workspaceId);

  const { data: workspacesData } = useWorkspaces(showTransferModal ? {} : null);
  const workspaceOptions = useMemo(
    () =>
      (workspacesData?.workspaces ?? [])
        .filter((ws) => ws.id !== device?.workspaceId)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((ws) => ({ label: `${ws.name} (#${ws.id})`, value: String(ws.id) })),
    [workspacesData, device?.workspaceId]
  );
  useDocumentTitle(device?.name ?? `Device #${deviceId}`);

  if (isLoading) {
    return (
      <Box>
        <Loader />
      </Box>
    );
  }

  if (error || !device) {
    return (
      <PageLoadError
        backAction={{ label: 'Back to devices', to: paths.devices }}
        onRetry={mutate}
      />
    );
  }

  const upgradeOsVersionOptions = getCompatibleOsVersionsForDevice(device.deviceType);
  const wipeStorageTooltip = getWipeStorageTooltip(device.core.reportedVersion);
  const wipeStorageUnsupported = wipeStorageTooltip !== undefined;

  /**
   * Posts a device action via the v2 client and shows a success toast.
   * Error handling and toasting is delegated to the `useMutation` hook.
   * Returns true when the action was accepted (202).
   */
  const postDeviceAction = async (
    json: DeviceActionBody,
    success: { title: string; message: string; durationMs?: number }
  ): Promise<boolean> => {
    try {
      await postAction(json);
      toast(ToastOptions.success(success.title, success.message, success.durationMs ?? 3000));
      return true;
    } catch {
      // The mutation hook already displays the error toast.
      return false;
    }
  };

  const onRestartApplicationDialogConfirmed = async () => {
    setShowRestartApplicationModal(false);
    await postDeviceAction(
      { action: 'RESTART-APPLICATION', fleetId },
      {
        title: 'Application restart scheduled',
        message: 'Application restart has been queued and will complete in the background.',
      }
    );
  };

  const onPruneDialogConfirmed = async () => {
    setShowPruneModal(false);
    await postDeviceAction(
      { action: 'PRUNE' },
      {
        title: 'Device system prune scheduled',
        message: 'Device system prune has been queued and will complete in the background.',
      }
    );
  };

  const onRebootDialogConfirmed = async () => {
    setShowRebootModal(false);
    await postDeviceAction(
      { action: 'REBOOT' },
      {
        title: 'Reboot scheduled',
        message: 'Device reboot has been queued and will complete in the background.',
      }
    );
  };

  const onWipePersistentStorageDialogConfirmed = async () => {
    setShowWipePersistentStorageModal(false);
    await postDeviceAction(
      { action: 'WIPE-PERSISTENT-STORAGE' },
      {
        title: 'Wipe persistent storage scheduled',
        message: 'Wipe persistent storage has been queued and will complete in the background.',
      }
    );
  };

  const handleQrCodeGenerate = async (device: DeviceResponseV5, filename = 'device-info.png') => {
    try {
      let deviceInfo = '';
      if (!device.deviceType) {
        deviceInfo = `1,${device.serialNumber},${device.macAddress}`;
      } else {
        deviceInfo = `2,${device.deviceType.vendor},${device.deviceType.model},${device.serialNumber},${device.macAddress}`;
      }

      const url = await toDataURL(deviceInfo, {
        margin: 1,
      });

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast(
        ToastOptions.success(
          'Device Label Generated',
          'The QR code label should be ready for download shortly.',
          3000
        )
      );
    } catch (err) {
      console.error(err);
      toast(
        ToastOptions.error(
          'Failed to generate device label',
          'Try again later or contact support if the issue persists.'
        )
      );
    }
  };

  // TODO(OSTree-Upgrade): This should be removed once all devices have been upgraded to Bootc
  const onUpgradeOsDialogConfirmed = async () => {
    if (!upgradeOsVersion) return;
    setShowUpgradeOsModal(false);
    await postDeviceAction(
      { action: 'UPGRADE-OS', imageTag: `emmaos-${upgradeOsVersion}` },
      {
        title: 'OS upgrade scheduled',
        message:
          'OS migration has been queued. This can take up to 10 minutes. The device will reboot once complete.',
        durationMs: 5000,
      }
    );
    setUpgradeOsVersion('');
  };

  const onTransferDeviceConfirmed = async () => {
    if (!targetWorkspaceId) return;
    setShowTransferModal(false);
    const result = await createDeviceTransfer({
      fromWorkspaceId: device.workspaceId,
      toWorkspaceId: targetWorkspaceId,
      deviceId: device.id,
    });
    if (result) {
      toast(
        ToastOptions.success(
          'Transfer requested',
          `Device transfer to workspace #${targetWorkspaceId} has been created.`,
          3000
        )
      );
    }
    setTargetWorkspaceId(undefined);
  };

  const deviceActionItems = canOperate
    ? buildDeviceActionItems({
        canWrite,
        device,
        wipeStorageTooltip,
        wipeStorageUnsupported,
        navigate,
        setShowTransferModal,
        setShowRestartApplicationModal,
        setShowPruneModal,
        setShowRebootModal,
        setShowWipePersistentStorageModal,
        handleQrCodeGenerate,
      })
    : undefined;

  return (
    <>
      <ConfirmationDialog
        body="This action cannot be undone!"
        header="Are you certain you wish to reboot the device?"
        isOpen={showRebootModal}
        onClose={() => setShowRebootModal(false)}
        onConfirmed={onRebootDialogConfirmed}
      />
      <ConfirmationDialog
        body="This action cannot be undone!"
        header="Are you certain you wish to prune the device?"
        isOpen={showPruneModal}
        onClose={() => setShowPruneModal(false)}
        onConfirmed={onPruneDialogConfirmed}
      />
      <ConfirmationSelectionDialog<number>
        body="This action cannot be undone!"
        getOptionKey={(option) => String(option)}
        getOptionLabel={(option) => {
          const fleetName = device.fleets.find((fleet) => fleet.id === option)?.name;
          return fleetName ? `${fleetName} (#${option})` : `Fleet #${option}`;
        }}
        header="Are you certain you wish to restart the application?"
        isOpen={showRestartApplicationModal}
        onClose={() => setShowRestartApplicationModal(false)}
        onConfirmed={onRestartApplicationDialogConfirmed}
        onSelectedChange={(value) => {
          setFleetId(value ?? undefined);
        }}
        selectedValue={fleetId ?? null}
        selectionOptions={device.fleets.map((fleet) => fleet.id)}
        selectionTitle="Please select the fleet to restart the application for."
      />
      <ConfirmationDialog
        body="This will permanently delete all data in the persistent storage directory. This action cannot be undone!"
        header="Are you certain you wish to wipe the persistent storage?"
        isOpen={showWipePersistentStorageModal}
        onClose={() => setShowWipePersistentStorageModal(false)}
        onConfirmed={onWipePersistentStorageDialogConfirmed}
      />
      {/* TODO(OSTree-Upgrade): This should be removed once all devices have been upgraded to Bootc */}
      <ConfirmationSelectionDialog<string>
        body="This will migrate the device to the next major emmaOS version. This operation can take
              up to 10 minutes and the device will reboot once complete. This action cannot be
              undone!"
        getOptionKey={(option) => option}
        getOptionLabel={(option) => option}
        header="Migrate to next major emmaOS version"
        isOpen={showUpgradeOsModal}
        onClose={() => setShowUpgradeOsModal(false)}
        onConfirmed={onUpgradeOsDialogConfirmed}
        onSelectedChange={(value) => setUpgradeOsVersion(value ?? '')}
        selectedValue={upgradeOsVersion ?? null}
        selectionOptions={upgradeOsVersionOptions}
        selectionPlaceholder="Select version"
        selectionTitle="Target Version"
      />
      <ConfirmationSelectionDialog<number>
        body={`Transfer device #${device.id} from workspace #${device.workspaceId} to another
              workspace. The owner of the target workspace will need to approve this transfer.`}
        getOptionKey={(option) => String(option)}
        getOptionLabel={(option) => {
          const found = workspaceOptions.find((ws) => ws.value === String(option));
          return found?.label ?? String(option);
        }}
        header="Transfer Device"
        isOpen={showTransferModal}
        onClose={() => {
          setShowTransferModal(false);
          setTargetWorkspaceId(undefined);
        }}
        onConfirmed={onTransferDeviceConfirmed}
        onSelectedChange={(value) => setTargetWorkspaceId(value ?? undefined)}
        selectedValue={targetWorkspaceId ?? null}
        selectionOptions={workspaceOptions.map((ws) => Number.parseInt(ws.value, 10))}
        selectionPlaceholder="Search workspaces..."
        selectionTitle="Target Workspace"
      />
      <Box>
        <BreadCrumb
          crumbs={[
            { label: 'Workspaces', path: paths.workspaces },
            {
              label: `Workspace #${device.workspaceId}`,
              path: paths.workspaceById(device.workspaceId),
            },
            {
              label: `Device #${device.id}`,
              path: paths.deviceById(device.id),
            },
          ]}
        />
        <SubNavigationBar
          actions={deviceActionItems ? { header: 'Actions', items: deviceActionItems } : undefined}
          options={[
            { label: 'Details', path: paths.deviceById(device.id) },
            { label: 'Files', path: paths.deviceFiles(device.id) },
            { label: 'Deployments', path: paths.deviceDeployments(device.id) },
            { label: 'Rollout Windows', path: paths.deviceRolloutWindows(device.id) },
            { label: 'SSH Keys', path: paths.deviceSshKeys(device.id) },
            { label: 'Network Configs', path: paths.deviceNetworkConfigs(device.id) },
            { label: 'Action Log', path: paths.deviceActionLog(device.id) },
          ]}
        />

        <Routes>
          <Route
            element={
              <DeviceDetails
                device={device}
                onUpgradeOs={() => setShowUpgradeOsModal(true)}
                refresh={mutate}
              />
            }
            path="/"
          />
          <Route
            element={
              <DeviceFileTable
                actionOnErr={actionOnErr}
                deviceId={device.id}
                workspaceId={device.workspaceId}
              />
            }
            path="files"
          />
          <Route element={<DeviceDeployments device={device} />} path="deployments" />
          <Route
            element={<DeviceRolloutWindows deviceId={device.id} workspaceId={device.workspaceId} />}
            path="rollout-windows"
          />
          <Route element={<DeviceSshKeys deviceId={device.id} />} path="ssh-keys" />
          <Route
            element={
              <DeviceNetworkConfigs
                canWrite={canWrite}
                deviceId={device.id}
                workspaceId={device.workspaceId}
              />
            }
            path="network-configs"
          />
          <Route
            element={
              <DeviceActionLog
                deviceId={device.id}
                reportedCoreVersion={device.core.reportedVersion}
              />
            }
            path="action-log"
          />
        </Routes>
      </Box>
    </>
  );
};

export const DevicePage = () => {
  const { deviceId } = useParams();

  if (!deviceId || !Number.parseInt(deviceId, 10)) {
    return <Navigate to={paths.devices} />;
  }

  return <DevicePageContent deviceId={Number.parseInt(deviceId, 10)} />;
};
